import React from 'react';
import { useIntl } from 'react-intl';
import useLanguageContext from '../../hooks/useLanguageContext';
import SupportedLanguages from '../../setup/intl/enums';
import { IconArrowForward, IconBank, IconBox, IconChevronLeft, IconDelete } from '../../assets/svg/icons';
import ScPageHeader from '../../components/molecules/ScPageHeader';
import ScCard from '../../components/atoms/ScCard';
import ScSectionHeader from '../../components/molecules/ScSectionHeader';
import ScButton from '../../components/atoms/ScButton';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import Badge from '../../components/atoms/Badge';
import { BadgeTone } from '../../components/atoms/Badge/enums';
import { CartLine, Product, QuoteView } from './types';
import { iqd } from './format';
import { cartSubtotal, tierPrice } from './pricing';
import { unitForms } from './liveData';
import { ProductImage } from './components';
import { StyledOrderGrid, StyledStickyCol } from './styles';
import {
  StyledCartLine,
  StyledCartMid,
  StyledCartName,
  StyledCartThumb,
  StyledCartUnit,
  StyledDivider,
  StyledEmptyCartCard,
  StyledLineTotal,
  StyledQtyMini,
  StyledRemoveBtn,
  StyledStepBtn,
  StyledStepper,
  StyledSummaryFootnote,
  StyledSummaryNote,
  StyledSumRow,
} from './styles2';

interface ScreenCartProps {
  lines: CartLine[];
  quote?: QuoteView;
  quoteLoading?: boolean;
  quoteError?: boolean;
  onBack: () => void;
  onOpenProduct: (id: string) => void;
  onStepUp: (p: Product) => void;
  onStepDown: (p: Product) => void;
  onRemove: (id: string) => void;
  onCheckout: () => void;
}

const ScreenCart: React.FC<ScreenCartProps> = ({
  lines,
  quote,
  quoteLoading,
  quoteError,
  onBack,
  onOpenProduct,
  onStepUp,
  onStepDown,
  onRemove,
  onCheckout,
}) => {
  const intl = useIntl();
  const { language } = useLanguageContext();
  const isAr = language === SupportedLanguages.Arabic;
  const cur = intl.formatMessage({ id: 'marketplace.currency', defaultMessage: 'IQD' });
  // Subtotal is client-side reliable; server quote is the cross-check.
  const subtotal = quote ? quote.subtotalIqd : cartSubtotal(lines);
  // Freight + total are SERVER-OWNED (quote.freightIqd / quote.totalIqd). While
  // the first quote loads show '…'; on error show subtotal + a muted note and
  // never block the cart. One PO per merchant, so freight is per-PO.
  const hasQuote = !!quote;
  const perMerchant = quote ? quote.merchants.length > 1 : false;
  const dash = intl.formatMessage({ id: 'marketplace.freight.loading', defaultMessage: '…' });
  const freightDisplay = hasQuote
    ? quote!.freightIqd === 0
      ? intl.formatMessage({ id: 'marketplace.freight.free', defaultMessage: 'Free delivery' })
      : `${iqd(quote!.freightIqd)} ${cur}`
    : dash;
  const totalDisplay = hasQuote
    ? `${iqd(quote!.totalIqd)} ${cur}`
    : quoteError
    ? `${iqd(subtotal)} ${cur}`
    : dash;

  return (
    <>
      <ScPageHeader
        title={intl.formatMessage({ id: 'marketplace.cart.title', defaultMessage: 'Your cart' })}
        subtitle={intl.formatMessage({ id: 'marketplace.cart.subtitle', defaultMessage: '{n, plural, one {# supplier item ready to order} other {# supplier items ready to order}}' }, { n: lines.length })}
        actions={
          <ScButton kind={ButtonKind.Ghost} icon={IconChevronLeft} onClick={onBack}>
            {intl.formatMessage({ id: 'marketplace.continueShopping', defaultMessage: 'Continue shopping' })}
          </ScButton>
        }
      />

      {lines.length === 0 ? (
        <ScCard pad={false}>
          <StyledEmptyCartCard>
            <div><IconBox /></div>
            <div style={{ fontWeight: 700, fontSize: 'var(--t-lg)' }}>{intl.formatMessage({ id: 'marketplace.cart.empty', defaultMessage: 'Your cart is empty' })}</div>
            <div className="muted" style={{ fontSize: 'var(--t-sm)', margin: '6px 0 var(--s-5)' }}>{intl.formatMessage({ id: 'marketplace.cart.emptyHint', defaultMessage: 'Browse the marketplace and add products in bulk.' })}</div>
            <ScButton kind={ButtonKind.Primary} onClick={onBack}>{intl.formatMessage({ id: 'marketplace.browse', defaultMessage: 'Browse marketplace' })}</ScButton>
          </StyledEmptyCartCard>
        </ScCard>
      ) : (
        <StyledOrderGrid $side="360px">
          <ScCard pad={false}>
            {lines.map(({ product: p, qty }, i) => {
              const name = isAr ? p.nameAr || p.name : p.name;
              const unit = tierPrice(p, qty);
              const cartUnitForms = unitForms(p.stockUnit, language);
              return (
                <StyledCartLine key={p.id} $divided={i < lines.length - 1}>
                  <StyledCartThumb $hue={p.hue} onClick={() => onOpenProduct(p.id)}>
                    <ProductImage product={p} glyphScale={0.7} />
                  </StyledCartThumb>
                  <StyledCartMid>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Badge tone={BadgeTone.Ink}>{p.brand}</Badge>
                      {p.lead && <span className="muted" style={{ fontSize: 11 }}>{p.lead}</span>}
                    </div>
                    <StyledCartName>{name}</StyledCartName>
                    <StyledCartUnit className="mono tnum">{iqd(unit)} {intl.formatMessage({ id: 'marketplace.iqdPerUnit', defaultMessage: 'IQD / {unit}' }, { unit: cartUnitForms.unit })}</StyledCartUnit>
                  </StyledCartMid>
                  <StyledStepper $h={38}>
                    <StyledStepBtn $w={34} style={{ fontSize: 18 }} type="button" onClick={() => onStepDown(p)} aria-label={intl.formatMessage({ id: 'marketplace.decrease', defaultMessage: 'Decrease quantity' })}>−</StyledStepBtn>
                    <StyledQtyMini className="tnum">{qty}</StyledQtyMini>
                    <StyledStepBtn $w={34} style={{ fontSize: 18 }} type="button" onClick={() => onStepUp(p)} aria-label={intl.formatMessage({ id: 'marketplace.increase', defaultMessage: 'Increase quantity' })}>+</StyledStepBtn>
                  </StyledStepper>
                  <StyledLineTotal>
                    <div className="mono tnum">{iqd(unit * qty)}</div>
                    <div>{cur}</div>
                  </StyledLineTotal>
                  <StyledRemoveBtn type="button" onClick={() => onRemove(p.id)} title={intl.formatMessage({ id: 'marketplace.remove', defaultMessage: 'Remove' })} aria-label={intl.formatMessage({ id: 'marketplace.removeNamed', defaultMessage: 'Remove {name}' }, { name })}>
                    <IconDelete />
                  </StyledRemoveBtn>
                </StyledCartLine>
              );
            })}
          </ScCard>

          <StyledStickyCol>
            <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
              <ScSectionHeader title={intl.formatMessage({ id: 'marketplace.orderSummary', defaultMessage: 'Order summary' })} />
              <StyledSumRow><span>{intl.formatMessage({ id: 'marketplace.subtotalItems', defaultMessage: 'Subtotal · {n, plural, one {# item} other {# items}}' }, { n: lines.length })}</span><span className="mono tnum">{iqd(subtotal)} {cur}</span></StyledSumRow>
              <StyledSumRow aria-busy={quoteLoading && !hasQuote ? true : undefined}>
                <span>{intl.formatMessage({ id: 'marketplace.freightHub', defaultMessage: 'Freight to Sandoog hub' })}</span>
                <span className="mono tnum">{freightDisplay}</span>
              </StyledSumRow>
              {quoteError && !hasQuote && (
                <StyledSummaryNote className="muted">{intl.formatMessage({ id: 'marketplace.freight.atCheckout', defaultMessage: 'Freight calculated at checkout' })}</StyledSummaryNote>
              )}
              {perMerchant && (
                <StyledSummaryNote className="muted tnum">{intl.formatMessage({ id: 'marketplace.freight.perPo', defaultMessage: 'Freight is charged per supplier · {n} purchase orders' }, { n: quote!.merchants.length })}</StyledSummaryNote>
              )}
              <StyledSumRow><span>{intl.formatMessage({ id: 'marketplace.importHandling', defaultMessage: 'Import & handling' })}</span><span>{intl.formatMessage({ id: 'marketplace.included', defaultMessage: 'Included' })}</span></StyledSumRow>
              <StyledDivider />
              <StyledSumRow $big><span>{intl.formatMessage({ id: 'marketplace.total', defaultMessage: 'Total' })}</span><span className="mono tnum">{totalDisplay}</span></StyledSumRow>
              <ScButton kind={ButtonKind.Primary} size={ButtonSize.Large} full iconRight={IconArrowForward} style={{ marginTop: 'var(--s-4)' }} onClick={onCheckout}>
                {intl.formatMessage({ id: 'marketplace.proceedCheckout', defaultMessage: 'Proceed to checkout' })}
              </ScButton>
              <StyledSummaryFootnote>
                <IconBank /><span>{intl.formatMessage({ id: 'marketplace.payFootnote', defaultMessage: 'Wallet · transfer · net-30 available' })}</span>
              </StyledSummaryFootnote>
            </ScCard>
          </StyledStickyCol>
        </StyledOrderGrid>
      )}
    </>
  );
};

export default ScreenCart;
